import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, ClipboardList, CloudUpload, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import Sheet from '@/features/chat/components/Sheet';
import { DebtDelta, DebtText, ScreenHeader, Stat, Stepper } from '@/components/ui';
import { apiErrorMessage, isNetworkError } from '@/lib/api';
import { clearShiftDrafts, draftHasWork, loadDraft } from '@/lib/drafts';
import { GEO_DENIED_HINT } from '@/lib/geo';
import { flushQueue, useQueue, type QueuedAction } from '@/lib/queue';
import { SHIFT_KEY, writeShiftCache } from '@/lib/shift-cache';
import { flushTrack, retryGps, useTrack } from '@/lib/track';
import { formatDuration, formatKop, formatQty, formatTime, isFractionalUnit, plural, uid } from '@/lib/format';
import { finishShift, type ActiveShift, type ShiftLoad, type ShiftPoint } from '@/services/courier';

type StationState = 'done' | 'pending' | 'open' | 'todo';

export function RouteScreen({ data }: { data: ActiveShift }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const queue = useQueue((s) => s.items);
  const flushing = useQueue((s) => s.flushing);
  const [finishOpen, setFinishOpen] = useState(false);
  const { shift, points } = data;

  const pendingClose = new Set<string>();
  for (const q of queue) if (q.kind === 'close' && q.shiftId === shift.id) pendingClose.add(q.pointId);
  // «Начата» — только если на точке что-то набрали или сняли фото: открыть и выйти — не начать.
  const started = (p: ShiftPoint) => !!p.visit?.hasPhoto || draftHasWork(loadDraft(shift.id, p.id));
  const stateOf = (p: ShiftPoint): StationState =>
    pendingClose.has(p.id) ? 'pending' : p.visit?.status === 'DONE' ? 'done' : started(p) ? 'open' : 'todo';
  const doneCount = points.filter((p) => isPassed(stateOf(p))).length;

  return (
    <>
      <ScreenHeader
        title={shift.routeName}
        subtitle={`${shift.cityName} · с ${formatTime(shift.startedAt)} · ${formatDuration(Date.now() - new Date(shift.startedAt).getTime())}`}
        right={
          <div className="mr-2 flex items-center gap-2">
            <GpsBadge />
            <span className="rounded-lg bg-orange-50 px-2.5 py-1 text-[13px] font-semibold tabular-nums text-orange-700">
              {doneCount}/{points.length}
            </span>
          </div>
        }
      />
      <div className="mx-auto w-full max-w-md space-y-3 px-4 py-4">
        <section className="card grid grid-cols-4 gap-2 p-4">
          <Stat label="Отгружено" value={formatKop(shift.deliveredTotal)} />
          <Stat label="Возвраты" value={formatKop(shift.returnedTotal)} />
          <Stat label="Получено" value={formatKop(shift.collectedTotal)} />
          <Stat label="Долг" value={<DebtDelta value={shift.debtTotal} />} />
        </section>

        <GpsWarning />
        {data.load && <LoadCard load={data.load} queue={queue} shiftId={shift.id} />}

        {pendingClose.size > 0 && (
          <section className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 pl-4">
            <CloudUpload size={18} className="shrink-0 text-amber-700" />
            <div className="min-w-0 flex-1 text-[13px] text-amber-900">
              {pendingClose.size} {plural(pendingClose.size, 'точка ждёт', 'точки ждут', 'точек ждут')} отправки — отправим, как появится связь
            </div>
            <button type="button" className="btn-sm btn bg-white text-amber-900" onClick={() => void flushQueue()} disabled={flushing}>
              {flushing ? <Loader2 size={15} className="animate-spin" /> : 'Отправить'}
            </button>
          </section>
        )}

        <ol className="card overflow-hidden py-1">
          {points.map((p, i) => {
            const st = stateOf(p);
            const passed = isPassed(st);
            return (
              <li key={p.id} className="relative">
                {/* Перегон красится, когда пройдены обе станции: курьер едет не обязательно по порядку. */}
                {i > 0 && (
                  <span className={clsx('absolute left-[27px] top-0 h-1/2 w-0.5', passed && isPassed(stateOf(points[i - 1]!)) ? 'bg-orange' : 'bg-ink/10')} />
                )}
                {i < points.length - 1 && (
                  <span className={clsx('absolute bottom-0 left-[27px] h-1/2 w-0.5', passed && isPassed(stateOf(points[i + 1]!)) ? 'bg-orange' : 'bg-ink/10')} />
                )}
                <button type="button" className="relative flex w-full items-center gap-3 py-3 pl-4 pr-3 text-left active:bg-ink/[0.03]" onClick={() => navigate(`/point/${p.id}`)}>
                  <Station state={st} index={i + 1} />
                  <div className="min-w-0 flex-1">
                    <div className={clsx('truncate text-[15px] font-medium', st === 'done' ? 'text-ink/60' : 'text-ink')}>{p.name}</div>
                    <div className="truncate text-[13px] text-ink/50">
                      {st === 'pending'
                        ? 'Ждёт отправки'
                        : st === 'done' && p.visit
                          ? `${formatKop(p.visit.deliveredTotal)} · получено ${formatKop(p.visit.paidAmount)}`
                          : st === 'open'
                            ? p.visit
                              ? `Начата в ${formatTime(p.visit.openedAt)} — не отмечена`
                              : 'Начата — не отмечена'
                            : [p.address, p.hours].filter(Boolean).join(' · ') || 'Адрес не указан'}
                    </div>
                  </div>
                  {st !== 'done' && st !== 'pending' && p.debt > 0 && (
                    <span className="shrink-0 rounded-md bg-red-50 px-1.5 py-0.5 text-[12px] font-medium tabular-nums text-red-700">долг {formatKop(p.debt)}</span>
                  )}
                  <ChevronRight size={18} className="shrink-0 text-ink/25" />
                </button>
              </li>
            );
          })}
        </ol>

        <button type="button" className="btn-danger w-full" onClick={() => setFinishOpen(true)}>
          Завершить маршрут
        </button>
      </div>

      {finishOpen && (
        <FinishSheet
          data={data}
          remaining={points.length - doneCount}
          queued={pendingClose.size}
          onClose={() => setFinishOpen(false)}
          onFinished={() => {
            clearShiftDrafts(shift.id);
            writeShiftCache(null);
            qc.setQueryData(SHIFT_KEY, null);
            void qc.invalidateQueries({ queryKey: ['stats'] });
            void qc.invalidateQueries({ queryKey: ['me'] });
          }}
        />
      )}
    </>
  );
}

const isPassed = (st: StationState) => st === 'done' || st === 'pending';

/** Остаток в машине: накладная минус то, что отдали на точках, ждущих отправки. */
function remainingWithQueue(load: ShiftLoad, queue: QueuedAction[], shiftId: string): Map<string, number> {
  const spent = new Map<string, number>();
  for (const q of queue) {
    if (q.kind !== 'close' || q.shiftId !== shiftId) continue;
    for (const l of q.body.lines) spent.set(l.itemId, (spent.get(l.itemId) ?? 0) + l.delivered);
    for (const s of q.body.swaps ?? []) spent.set(s.toItemId, (spent.get(s.toItemId) ?? 0) + s.toQty);
  }
  return new Map(load.lines.map((l) => [l.itemId, Math.max(0, Math.round((l.remaining - (spent.get(l.itemId) ?? 0)) * 1000) / 1000)]));
}

function LoadCard({ load, queue, shiftId }: { load: ShiftLoad; queue: QueuedAction[]; shiftId: string }) {
  const [open, setOpen] = useState(false);
  const left = remainingWithQueue(load, queue, shiftId);
  const lines = load.lines.filter((l) => l.issued > 0);
  const inCar = lines.filter((l) => (left.get(l.itemId) ?? 0) > 0).length;
  return (
    <section className="card overflow-hidden">
      <button type="button" className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-ink/[0.03]" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <ClipboardList size={18} className="shrink-0 text-orange-700" />
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-medium text-ink">Накладная №{load.number}</div>
          <div className="text-[13px] text-ink/50">
            В машине {inCar} из {lines.length} {plural(lines.length, 'позиции', 'позиций', 'позиций')}
          </div>
        </div>
        <ChevronDown size={18} className={clsx('shrink-0 text-ink/30 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="list-rows border-t border-line">
          {lines.map((l) => {
            const rest = left.get(l.itemId) ?? 0;
            return (
              <li key={l.itemId} className="flex items-baseline justify-between gap-3 px-4 py-2.5 text-[14px]">
                <span className={clsx('min-w-0', rest > 0 ? 'text-ink' : 'text-ink/40')}>{l.name}</span>
                <span className="shrink-0 tabular-nums text-ink/60">
                  <span className={clsx('font-medium', rest > 0 ? 'text-ink' : 'text-ink/40')}>{formatQty(rest)}</span> из {formatQty(l.issued)} {l.unit}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function GpsBadge() {
  const status = useTrack((s) => s.status);
  const accuracy = useTrack((s) => s.accuracy);
  const label =
    status === 'ok'
      ? `GPS ±${accuracy ?? '?'} м`
      : status === 'searching'
        ? 'Ищем GPS'
        : status === 'weak'
          ? 'Слабый GPS'
          : status === 'denied'
            ? 'GPS запрещён'
            : status === 'unavailable' || status === 'unsupported'
              ? 'Нет GPS'
              : null;
  if (!label) return null;
  // Погрешность больше 100 м — линия трека по таким фиксам не рисуется.
  const tone = status === 'ok' && (accuracy ?? 0) <= 100 ? 'ok' : status === 'searching' ? 'muted' : status === 'ok' || status === 'weak' ? 'warn' : 'bad';
  return (
    <span
      className={clsx(
        'flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-medium',
        tone === 'ok' ? 'bg-emerald-50 text-emerald-700' : tone === 'muted' ? 'bg-ink/[0.05] text-ink/55' : tone === 'warn' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700',
      )}
      title={label}
    >
      <span
        className={clsx(
          'h-1.5 w-1.5 rounded-full',
          tone === 'ok' ? 'bg-emerald-500' : tone === 'muted' ? 'animate-pulse bg-ink/40' : tone === 'warn' ? 'bg-amber-500' : 'bg-red-500',
        )}
      />
      <span className="max-[359px]:hidden">{label}</span>
    </span>
  );
}

/** Почему GPS не пишет трек и что сделать курьеру. */
function GpsWarning() {
  const status = useTrack((s) => s.status);
  if (status === 'weak') {
    return (
      <p role="status" className="rounded-2xl bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
        GPS долго ищет спутники — маршрут пишется с пропусками. Выйдите на открытое место: приложение продолжит само.
      </p>
    );
  }
  if (status !== 'denied' && status !== 'unavailable' && status !== 'unsupported') return null;
  const text =
    status === 'denied'
      ? `Разрешите геолокацию в настройках браузера — без неё руководитель не видит маршрут и точки не отметить. ${GEO_DENIED_HINT}`
      : status === 'unavailable'
        ? 'Телефон не отдаёт местоположение — включите геолокацию (службы геолокации) в настройках телефона и вернитесь в приложение.'
        : 'Этот браузер не умеет определять местоположение — откройте приложение в Chrome (Android) или Safari (iPhone).';
  return (
    <section role="alert" className="rounded-2xl bg-red-50 px-4 py-3 text-[13px] text-red-800">
      <p>{text}</p>
      {status !== 'unsupported' && (
        <button type="button" className="btn-sm btn mt-2.5 bg-white text-red-800 ring-1 ring-red-200" onClick={retryGps}>
          Проверить снова
        </button>
      )}
    </section>
  );
}

function Station({ state, index }: { state: StationState; index: number }) {
  if (state === 'done') {
    return (
      <span className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange text-white">
        <Check size={14} strokeWidth={3} />
      </span>
    );
  }
  if (state === 'pending') {
    return (
      <span className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-amber-400 bg-white text-amber-600">
        <CloudUpload size={12} strokeWidth={2.5} />
      </span>
    );
  }
  if (state === 'open') {
    return (
      <span className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-orange bg-white">
        <span className="h-2 w-2 rounded-full bg-orange" />
      </span>
    );
  }
  return (
    <span className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-ink/15 bg-white text-[11px] font-semibold tabular-nums text-ink/45">
      {index}
    </span>
  );
}

function FinishSheet({
  data,
  remaining,
  queued,
  onClose,
  onFinished,
}: {
  data: ActiveShift;
  remaining: number;
  queued: number;
  onClose: () => void;
  onFinished: () => void;
}) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const add = useQueue((s) => s.add);
  const queueItems = useQueue((s) => s.items);
  const { shift, load } = data;
  const [step, setStep] = useState<'summary' | 'count'>('summary');
  const expected = load ? remainingWithQueue(load, queueItems, shift.id) : null;
  const countLines = load ? load.lines.filter((l) => (expected?.get(l.itemId) ?? 0) > 0) : [];
  const [counted, setCounted] = useState<Record<string, number>>(() => Object.fromEntries(countLines.map((l) => [l.itemId, expected?.get(l.itemId) ?? 0])));
  const shortage = countLines.filter((l) => (counted[l.itemId] ?? 0) < (expected?.get(l.itemId) ?? 0) - 1e-9).length;

  const finish = async () => {
    setBusy(true);
    const report = load ? load.lines.map((l) => ({ itemId: l.itemId, leftover: counted[l.itemId] ?? 0 })) : undefined;
    const body = { comment: comment.trim() || null, clientRequestId: uid(), ...(report ? { report } : {}) };
    try {
      await flushQueue();
      // Хвост трека — пока смена открыта (опоздавший сервер примет ещё полчаса).
      await flushTrack({ force: true });
      const stillQueued = useQueue.getState().items.some((q) => q.shiftId === shift.id);
      if (stillQueued) {
        add({ id: body.clientRequestId, kind: 'finish', shiftId: shift.id, body, createdAt: Date.now() });
        toast('Нет связи — завершим смену, как только отправим точки', { duration: 6000 });
      } else {
        try {
          await finishShift(body);
          toast.success('Маршрут завершён. Спасибо за работу!');
        } catch (err) {
          if (!isNetworkError(err)) {
            toast.error(apiErrorMessage(err));
            return;
          }
          add({ id: body.clientRequestId, kind: 'finish', shiftId: shift.id, body, createdAt: Date.now() });
          toast('Нет связи — завершим смену, как появится сеть', { duration: 6000 });
        }
      }
      onFinished();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  if (step === 'count' && load) {
    return (
      <Sheet title="Остаток в машине" onClose={() => !busy && onClose()}>
        <p className="-mt-2 text-[13px] text-ink/55">
          Пересчитайте, что осталось по накладной №{load.number}. Если меньше расчётного — уменьшите: разница уйдёт в недостачу.
        </p>
        {countLines.length === 0 ? (
          <p className="mt-4 rounded-xl bg-emerald-50 px-3 py-2.5 text-[14px] text-emerald-800">Всё роздано — остатка нет.</p>
        ) : (
          <ul className="mt-3 divide-y divide-line rounded-xl ring-1 ring-line">
            {countLines.map((l) => {
              const exp = expected?.get(l.itemId) ?? 0;
              return (
                <li key={l.itemId} className="px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-3 text-[14px]">
                    <span className="min-w-0 text-ink">{l.name}</span>
                    <span className="shrink-0 text-[12px] tabular-nums text-ink/45">
                      должно быть {formatQty(exp)} {l.unit}
                    </span>
                  </div>
                  <div className="mt-1.5">
                    <Stepper
                      label={`${l.name}: осталось`}
                      value={counted[l.itemId] ?? 0}
                      max={exp}
                      fractional={isFractionalUnit(l.unit)}
                      onChange={(v) => setCounted((c) => ({ ...c, [l.itemId]: v }))}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {shortage > 0 && (
          <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900">
            Недостача по {shortage} {plural(shortage, 'позиции', 'позициям', 'позициям')} — руководитель увидит её в отчёте по накладной.
          </p>
        )}
        <button type="button" className="btn-danger mt-5 w-full" onClick={finish} disabled={busy}>
          {busy && <Loader2 size={18} className="animate-spin" />}
          Сдать остаток и завершить
        </button>
        <button type="button" className="btn-ghost mt-2 w-full" onClick={() => setStep('summary')} disabled={busy}>
          Назад
        </button>
      </Sheet>
    );
  }

  return (
    <Sheet title="Завершить маршрут?" onClose={() => !busy && onClose()}>
      <div className="grid grid-cols-2 gap-3 rounded-xl bg-paper p-4">
        <Stat label="Точки" value={`${data.points.length - remaining} из ${data.points.length}`} />
        <Stat label="Отгружено" value={formatKop(shift.deliveredTotal)} />
        <Stat label="Получено" value={formatKop(shift.collectedTotal)} />
        <Stat label="Долг за смену" value={<DebtText value={shift.debtTotal} />} />
      </div>
      {remaining > 0 && (
        <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900">
          Не пройдено {remaining} {plural(remaining, 'точка', 'точки', 'точек')} — они не попадут в итоги смены.
        </p>
      )}
      {queued > 0 && (
        <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900">
          {queued} {plural(queued, 'точка ещё не отправлена', 'точки ещё не отправлены', 'точек ещё не отправлены')} — сначала отправим их.
        </p>
      )}
      <label className="mt-4 block">
        <span className="label">Комментарий руководителю</span>
        <textarea className="input resize-none" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} placeholder="Необязательно" />
      </label>
      {load ? (
        <button type="button" className="btn-danger mt-5 w-full" onClick={() => setStep('count')}>
          Дальше: пересчитать остаток
        </button>
      ) : (
        <button type="button" className="btn-danger mt-5 w-full" onClick={finish} disabled={busy}>
          {busy && <Loader2 size={18} className="animate-spin" />}
          Завершить маршрут
        </button>
      )}
      <button type="button" className="btn-ghost mt-2 w-full" onClick={onClose} disabled={busy}>
        Продолжить работу
      </button>
    </Sheet>
  );
}
